import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  MegaphoneIcon,
  AcademicCapIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import partnerHero from '../../assets/img/Partner image/handcheck.jpg';
import benefit1 from '../../assets/img/partners/1.png';
import benefit2 from '../../assets/img/partners/2.png';
import benefit3 from '../../assets/img/partners/3.png';
import benefit4 from '../../assets/img/partners/4.png';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  GlassCard,
  PremiumCTA,
  inputClass,
  btnPrimaryClass,
} from '../premium/PremiumPageUI';
import { submitFormEmail } from '../../lib/submitFormEmail';

const benefits = [
  { title: 'Revenue Growth', description: 'Unlock new revenue streams and profitability through co-selling and referral programs.', icon: CurrencyDollarIcon, img: benefit1 },
  { title: 'Technical Support', description: 'Priority access to certified engineers, solution architects, and dedicated partner support.', icon: WrenchScrewdriverIcon, img: benefit2 },
  { title: 'Marketing Resources', description: 'Co-marketing campaigns, branded assets, and joint go-to-market initiatives.', icon: MegaphoneIcon, img: benefit3 },
  { title: 'Training & Certification', description: 'Comprehensive enablement paths and vendor-aligned certification programs.', icon: AcademicCapIcon, img: benefit4 },
];

const partnerTypes = [
  { title: 'Technology Partner', description: 'Integrate our solutions with your technology stack.', features: ['API Integration Support', 'Technical Documentation', 'Development Resources', 'Sandbox Access'] },
  { title: 'Solution Partner', description: 'Resell and implement our solutions for your clients.', features: ['Sales Enablement', 'Implementation Support', 'Partner Portal', 'Lead Generation'] },
  { title: 'Strategic Partner', description: 'Long-term collaboration with executive alignment.', features: ['Joint Business Planning', 'Executive Sponsorship', 'Custom Solutions', 'Priority Support'] },
];

const faqs = [
  { q: 'How long does the application process take?', a: 'Typically 2–3 weeks including review and initial alignment calls.' },
  { q: 'What are the partnership requirements?', a: 'Requirements vary by tier—we discuss specifics during your application review.' },
  { q: 'Is there a partnership fee?', a: 'Fees depend on partnership type and engagement level.' },
  { q: 'What support do partners receive?', a: 'Technical support, marketing resources, training, and dedicated account management.' },
];

export default function BecomePartner() {
  const [formData, setFormData] = useState({ companyName: '', contactName: '', email: '', phone: '', partnerType: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitFormEmail({
        formName: 'Partnership Application',
        fields: {
          companyName: formData.companyName,
          contactName: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          partnerType: formData.partnerType,
          message: formData.message,
        },
      });
      alert('Thank you for your interest! We will contact you soon.');
      setFormData({ companyName: '', contactName: '', email: '', phone: '', partnerType: '', message: '' });
    } catch (error) {
      alert(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell>
      <section className="relative pt-28 pb-0 overflow-hidden">
        <div className="absolute inset-0">
          <img src={partnerHero} alt="" className="w-full h-full object-cover opacity-30 dark:opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-slate-50/90 to-white dark:from-gray-900/95 dark:via-gray-900 dark:to-gray-900" />
        </div>
        <div className="relative">
          <PremiumHero
            badge="Partner Program"
            title="Become Our"
            highlight="Partner"
            subtitle="Join our ecosystem and unlock growth through technology alliances, co-innovation, and shared success."
            stats={[
              { value: '50+', label: 'Active Partners' },
              { value: '3', label: 'Partner Tiers' },
              { value: '24/7', label: 'Support' },
              { value: 'Global', label: 'Reach' },
            ]}
            actions={[
              { label: 'Apply Now', primary: true, onClick: () => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }) },
              { label: 'Strategic Alliances', href: '/partnerships' },
            ]}
          />
        </div>
      </section>

      <PremiumSection>
        <SectionHeader badge="Advantages" title="Partner" highlight="Benefits" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} whileHover={{ y: -6 }}>
                <GlassCard className="p-6 h-full text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#2563EB]/10 to-cyan-500/10 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center">
                    <img src={b.img} alt="" className="h-8 w-auto object-contain dark:brightness-0 dark:invert" />
                  </div>
                  <Icon className="w-6 h-6 text-[#2563EB] mx-auto mb-3 hidden" />
                  <h3 className="font-bold text-slate-900 dark:text-white mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-gray-300">{b.description}</p>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      </PremiumSection>

      <PremiumSection alt>
        <SectionHeader badge="Models" title="Partnership" highlight="Types" />
        <div className="grid md:grid-cols-3 gap-6">
          {partnerTypes.map((type, i) => (
            <motion.div key={type.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} whileHover={{ y: -8 }}>
              <GlassCard className="p-8 h-full border-t-4 border-t-[#2563EB]">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{type.title}</h3>
                <p className="text-sm text-slate-600 dark:text-gray-300 mb-6">{type.description}</p>
                <ul className="space-y-3">
                  {type.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-600 dark:text-gray-300">
                      <CheckCircleIcon className="w-5 h-5 text-[#2563EB] flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection id="apply">
        <SectionHeader badge="Application" title="Apply for" highlight="Partnership" subtitle="Complete the form and our alliances team will reach out within 3 business days." />
        <GlassCard className="max-w-3xl mx-auto p-8 md:p-10" hover={false}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Company Name</label>
                <input type="text" name="companyName" required value={formData.companyName} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Contact Name</label>
                <input type="text" name="contactName" required value={formData.contactName} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Email</label>
                <input type="email" name="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Phone</label>
                <input type="tel" name="phone" required value={formData.phone} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Partnership Type</label>
              <select name="partnerType" required value={formData.partnerType} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} className={inputClass}>
                <option value="">Select type</option>
                <option value="technology">Technology Partner</option>
                <option value="solution">Solution Partner</option>
                <option value="strategic">Strategic Partner</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Message</label>
              <textarea name="message" rows={4} required value={formData.message} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} className={inputClass} />
            </div>
            <button type="submit" disabled={isSubmitting} className={`${btnPrimaryClass} w-full md:w-auto disabled:opacity-60`}>
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </GlassCard>
      </PremiumSection>

      <PremiumSection alt>
        <SectionHeader badge="FAQ" title="Common" highlight="Questions" />
        <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
          {faqs.map((faq) => (
            <GlassCard key={faq.q} className="p-6">
              <h3 className="font-bold text-slate-900 dark:text-white mb-2">{faq.q}</h3>
              <p className="text-sm text-slate-600 dark:text-gray-300">{faq.a}</p>
            </GlassCard>
          ))}
        </div>
      </PremiumSection>

      <PremiumCTA
        title="Grow Together With Nisunajni"
        subtitle="Explore our strategic vendor alliances or start your partner application today."
        primary={{ label: 'View Alliances', href: '/partnerships' }}
        secondary={{ label: 'Contact Sales', href: '/contact' }}
      />
    </PageShell>
  );
}
